import L, { Map, LatLng, latLng, LayerGroup, layerGroup, GeoJSON, geoJson, Polyline, Point, Marker } from "leaflet"
import { MapService } from "../../../core/services";

/**
 * Pomocná třída geometrické vrstvy mapy. Hlavně kreslí dráhu spoje.
 **/
export class RouteLayerService {
  private static readonly defaultGreyColor: string = "#959596"	// výchozí šedá
  private readonly arrowLength: number = 11;						// delka sipky v pixelech

  private map!: Map;
  private pathColor: string = RouteLayerService.defaultGreyColor;
  private geometry: GeoJSON<any> = geoJson();

  pathCoords: LatLng[] = []; 

  pathLayer!: LayerGroup;
  arrowLayer!: LayerGroup;

  /** ve kterém indexu v poli souřadnic se láme projetá a budoucí trasa */
  private sliceIndex: number = 0; 

  init(map: Map, mapService: MapService) {
    this.map = map;
    this.arrowLayer = layerGroup().addTo(this.map);
    this.pathLayer = layerGroup().addTo(this.map);

    mapService.pathToShow$.subscribe(data => {
      if (data.path.length > 0)
        this.addPath(data.path, data.position, data.type);
      else {
        this.clearArrows();
        this.pathLayer.clearLayers();
        this.pathCoords = [];
      }
    });
  }

  clearArrows() {
		this.arrowLayer?.clearLayers();
  }

  redrawPath = (activeMarker: Marker) => {
    if (this.pathCoords) {
          const coords: number[][] = this.pathCoords.map(ll => [ll.lng, ll.lat]);
          this.assignGeometry(coords, activeMarker.getLatLng());
          this.pathLayer.clearLayers();
          this.pathLayer.addLayer(this.geometry);

          this.arrowLayer.clearLayers();
          const slices = [this.pathCoords.slice(0, this.sliceIndex + 1), this.pathCoords.slice(this.sliceIndex)];
          this.drawDirectionArrows(slices[0], RouteLayerService.defaultGreyColor);
          this.drawDirectionArrows(slices[1], this.pathColor);
      };
  }

  private readonly addPath = (data: string, position: LatLng | undefined, type: string | undefined) => {
    this.pathColor = "#0088aa";   // bus
    if (type == undefined)
      this.pathColor = "#93d230"; // vlak
    else if (type == "A")
      this.pathColor = "#c03b30"; // MHD

    let coords: number[][] = [];
    let pos = data.indexOf('((');
    if (pos <= 0) {
      pos = data.indexOf('(') + 1;
      this.pathCoords = this.processLineString(data, pos, coords);
      this.assignGeometry(coords, position);
    }
    else {
      this.pathCoords = this.processMultiLineString(data, pos, coords);
      this.assignGeometry(coords, position);
    }

    this.pathLayer.clearLayers();
    this.pathLayer.addLayer(this.geometry);

    const slices = [this.pathCoords.slice(0, this.sliceIndex + 1), this.pathCoords.slice(this.sliceIndex)];
    this.drawDirectionArrows(slices[0], RouteLayerService.defaultGreyColor);
    this.drawDirectionArrows(slices[1], this.pathColor);
  }

  private processLineString(data: string, pos: number, coords: number[][]): LatLng[] {
    let coordsForArrows: LatLng[] = [];
    let coordString = data.slice(pos);
    let lineStrEnd = data.indexOf(')');
    coordString = coordString.slice(0, lineStrEnd - 1);
    const coordPairs = coordString.split(',');
    let i: number = coords.length;
    coordPairs.forEach((pair) => {
      let x = pair.split(' ');
      coords[i] = [];
      coords[i].push(parseFloat(x[0]), parseFloat(x[1]));
      coordsForArrows.push(latLng(coords[i][1], coords[i][0]));
      i++;
    })

    return coordsForArrows;
  }

  private processMultiLineString(data: string, pos: number, coords: number[][]): LatLng[] {
    let coordsForArrows: LatLng[] = [];
    let coordString = data.slice(pos);
    coordString = coordString.slice(2, coordString.indexOf('))'));

    // Převedení do jednoho LineStringu
    let linestrings: string[] = coordString.split('),(');
    linestrings.forEach(s => {
      coordsForArrows = coordsForArrows.concat(this.processLineString(s + ')', 0, coords));
    });

    coords = coordsForArrows.map(ll => [ll.lng, ll.lat]);

    return coordsForArrows;
  }

  private assignGeometry(coords: number[][], position: LatLng | undefined) {
    this.findClosestIndexWithin(coords, position);
    const slices = [coords.slice(0, this.sliceIndex + 1), coords.slice(this.sliceIndex, coords.length - 1)];
    
    const traveledFeature = {
      type: "Feature" as const,
      properties: { name: "traveled" },
      geometry: { type: "LineString" as const, coordinates: slices[0] }
    };

    const remainingFeature = {
      type: "Feature" as const,
      properties: { name: "remaining" },
      geometry: { type: "LineString" as const, coordinates: slices[1] }
    };

    const featureCollection = {
      type: "FeatureCollection" as const,
      features: [remainingFeature, traveledFeature]
    };

    this.geometry = geoJson(featureCollection, {
      style: (feature) => {
        return feature?.properties.name === "traveled"
          ? { color: RouteLayerService.defaultGreyColor, weight: 4 }
          : { color: this.pathColor, weight: 4 };
      }
    });
  }

  private findClosestIndexWithin(coords: number[][], position: LatLng | undefined, maxMeters: number = 500) {
    if (!position) {
      this.sliceIndex = 0;
      return;
    }

    let closestIndex = -1;
    let minDist = Number.POSITIVE_INFINITY;

    coords.forEach(([cLng, cLat], i) => {
      const candidate = L.latLng(cLat, cLng);
      const meters = position.distanceTo(candidate);
      if (meters < minDist) {
        minDist = meters;
        closestIndex = i;
      }
    });

    if (minDist == 0 || closestIndex == coords.length - 1)
      return; // nemožné, něco se opozdilo

    this.sliceIndex = minDist <= maxMeters ? closestIndex : 0;
  }

	private getPixelPosition(angle: number): Point {
		let radianBearing = (angle / 180) * Math.PI;
		let y = -this.arrowLength * Math.cos(radianBearing);
		let x = +this.arrowLength * Math.sin(radianBearing);
		return new Point(x, y);
	}

  private drawDirectionArrows(coords: LatLng[], color: string | null = null) {
    const zoom = this.map.getZoom();
    let lastArrowPoint = coords[0];
    let oldpoint = coords[0];
    let distance = 0;
    let arrowDistanceThreshold = 23456 / (zoom - 7);
    for (let i = 1; i < coords.length - 1; i++) {
      let point = coords[i];
      distance += oldpoint.distanceTo(point);
      if (distance > arrowDistanceThreshold || lastArrowPoint.distanceTo(point) > arrowDistanceThreshold) {
        let arrowMiddlePix = this.map.latLngToLayerPoint(oldpoint);
        let pointP = this.map.latLngToLayerPoint(point);
        arrowMiddlePix.x = (arrowMiddlePix.x + pointP.x) / 2;
        arrowMiddlePix.y = (arrowMiddlePix.y + pointP.y) / 2;

        let azimuth = this.getAzimuthDegrees(oldpoint, point);
        let leftArrowSideAngle = azimuth - 135;
        if (leftArrowSideAngle < 0)
          leftArrowSideAngle += 360;

        let rightArrowSideAngle = azimuth + 135;
        if (rightArrowSideAngle > 360)
          rightArrowSideAngle -= 360;

        let leftArrowSidePoint = this.getPixelPosition(leftArrowSideAngle);
        let rightArrowSidePoint = this.getPixelPosition(rightArrowSideAngle);

        leftArrowSidePoint = leftArrowSidePoint.add(arrowMiddlePix);
        rightArrowSidePoint = rightArrowSidePoint.add(arrowMiddlePix);

        lastArrowPoint = this.map.layerPointToLatLng(arrowMiddlePix);
        let arrowPoints: Array<LatLng> = [this.map.layerPointToLatLng(leftArrowSidePoint), lastArrowPoint, this.map.layerPointToLatLng(rightArrowSidePoint)];
        distance = 0;

        if (color)
          RouteLayerService.arrowsOptions.color = color;

        new Polyline(arrowPoints, RouteLayerService.arrowsOptions).addTo(this.arrowLayer);
      }

      oldpoint = point;
    }
  }

	private getAzimuthDegrees(coord1: LatLng, coord2: LatLng): number {
		const y = Math.sin(coord2.lng - coord1.lng) * Math.cos(coord2.lat);
		const x = Math.cos(coord1.lat) * Math.sin(coord2.lat) -
			Math.sin(coord1.lat) * Math.cos(coord2.lat) * Math.cos(coord2.lng - coord1.lng);
		const θ = Math.atan2(y, x);
		return (θ * 180 / Math.PI + 360) % 360; // in degrees
	}

	private static readonly arrowsOptions = {
		color: this.defaultGreyColor,
		opacity: 1.,
		curvature: 0,
		weight: 3
  };
}	
